import { createContext, useContext, useLayoutEffect, useState, type ComponentType, type ReactNode } from "react";

// Record pages hand their sections to the sidebar, which shows them in place of the app-wide links.

export type RecordNavItem = { id: string; label: string; icon: ComponentType<{ className?: string }> };

export type RecordNavState = {
  /** Where "back" goes, e.g. the list this record lives in. */
  back: { to: string; label: string };
  /** What the record is, e.g. "Organisation". */
  kind: string;
  title: string;
  items: RecordNavItem[];
  /** The active section id; items link to `#<id>`. */
  active: string;
};

type RecordNavContextValue = {
  state: RecordNavState | null;
  setState: (state: RecordNavState | null) => void;
};

const RecordNavContext = createContext<RecordNavContextValue>({ state: null, setState: () => {} });

export function RecordNavProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<RecordNavState | null>(null);
  return <RecordNavContext.Provider value={{ state, setState }}>{children}</RecordNavContext.Provider>;
}

/** The record nav the sidebar should show, or null for the app-wide links. */
export function useRecordNavState() {
  return useContext(RecordNavContext).state;
}

/** Called by a record page: shows its sections in the sidebar while it's mounted. */
export function useRecordNav({ back, kind, title, items, active }: RecordNavState) {
  const { setState } = useContext(RecordNavContext);

  // Layout effect, so the sidebar never paints the app links for a frame on the way into a record.
  useLayoutEffect(() => {
    setState({ back, kind, title, items, active });
  }, [setState, back.to, back.label, kind, title, items, active]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => () => setState(null), [setState]);
}
